<template>
  <FormController :steps :form :current-step :loading :disabled @next="next" @back="back" @resume="resumeForm">
    <template v-for="step in steps" #[step]>
      <slot :name="step" :form>

        <template v-if="step == 'review'">
          <VisaFormReview :form />
        </template>

        <template v-else-if="step == 'upload' && isVisaFormWithFiles(form)">
          <ImageUploadFields
            v-model="form.applicants"
            v-model:method="form.fileTransferMethod"
            :form-id="form.id"
            :types="form.product.photo_types!"
            ref="uploadFields"
          />
        </template>

        <template v-else-if="step == 'payment'">
          <PaymentFields v-model="form" />
        </template>

      </slot>
    </template>

    <template #footer v-if="shouldShowVariationAlert()">
      <VariationAlerts :variations="applicableVariations" :form />
    </template>

  </FormController>
</template>

<script setup lang="ts" generic="C extends CountryKey">
import { setChatContact } from "~/helpers/chat";
import { resizeArrayOfObject } from "~/helpers/general";
import type { CountryKey, Product, ResumeStatus, VisaForm } from "~/types";
import applicantFactory from "~/factories/applicant-factory";
import { ImageUploadFields } from "#components";
import { watchThrottled, useIntervalFn } from '@vueuse/core';
import { isClientValid } from "~/helpers/form-validation";
import type { UnwrapRef } from "vue";
import { getIp } from "~/helpers/http";
import visaFormFactory from "~/factories/visa-form-factory";
import { getApplicableVariations, isVisaFormWithFiles } from "~/helpers/visa-form";
import { setSentryUser } from "~/helpers/sentry";

type Step = typeof STEPS[number];

const STEPS = ['visa_select', 'details', 'review', 'upload', 'payment'] as const;
const AUTOSAVE_MS = 60_000;

const props = defineProps<{
  country: C;
  products: Product[];
  disabled?: boolean;
}>();

const branch = useBranch();
const { resumeStatus, resumeMechanism, resume } = useFormResume(props.country);
const { immediateSessionRecording } = useQueryFlags();
const { recordSession, identifyForSessionRecording } = useSessionRecording();
const slots = useSlots() as { [key in Step]: any };
const steps = STEPS.filter(s => slots[s]);

const form = reactive(visaFormFactory({ branch, country: props.country, products: props.products }));
const uploadFieldsRef = useTemplateRef('uploadFields');
const currentStep = ref(1);
const loading = ref(false);
const currentStepType = computed(() => steps[currentStep.value - 1]);
const clientValid = computed(() => isClientValid(form.client));
const applicableVariations = computed(() => getApplicableVariations(form));
const changed = ref(false);
const { save: _save, saving, saveable } = useFormSave(form);
const shouldSave = computed(() => changed.value && saveable.value);

useIntervalFn(saveIfNeeded, AUTOSAVE_MS);

watch(form, () => { changed.value = true; }, { deep: true });
watch(() => form.quantity, adjustApplicantCount);
watch(currentStep, setNewStep);
watch(() => form.meta.step, setCompletion);
watch(resumeStatus, resumeIfNeeded);
watch(resumeStatus, setMetaResumed);
watchThrottled(() => form.client, client => {
  if (!clientValid.value) return;
  setChatContact(client);
  setSentryUser(form.id, client);
  identifyForSessionRecording(client);
  recordSession();
  save();
}, { throttle: 5_000, deep: true });

onMounted(() => {
  setMetaLinks();
  setIp();
  if (immediateSessionRecording) recordSession();
});

async function next() {
  loading.value = true;
  try {
    if (currentStepType.value == 'upload') await upload();
    await saveIfNeeded();
  }
  finally {
    loading.value = false;
  }
  currentStep.value++;
}

function back() {
  currentStep.value--;
}

function resumeIfNeeded(status: ResumeStatus) {
  if (status == 'resumable' && resumeMechanism.value == 'query') {
    console.info('Resuming from query params');
    resumeForm();
  }
}

async function resumeForm() {
  const resumedForm = await resume();
  set(resumedForm);
  currentStep.value = 1;
}

function adjustApplicantCount(count: number) {
  const { applicants, country } = form;
  form.applicants = resizeArrayOfObject(applicants, count, () => {
    return applicantFactory({ country, branch });
  }) as typeof applicants;
}

async function upload() {
  await uploadFieldsRef.value!.upload();
}

function set(_form: VisaForm) {
  Object.assign(form, _form);
}

async function saveIfNeeded() {
  if (shouldSave.value) {
    await save();
  }
}

async function save() {
  if (saving.value) {
    console.warn('Already saving, skipping save call');
    return;
  }
  await _save();
  changed.value = false;
}

function setCompletion(step: number) {
  form.meta.completion = step / steps.length;
}

function setNewStep(step: number) {
  if (step > form.meta.step) {
    form.meta.step = step;
  }
}

function setMetaLinks() {
  form.meta.url = useRouting().fullUrl;
  form.meta.referrer = document.referrer;
  form.meta.utm = useUtm();
}

async function setIp() {
  form.meta.clientIp = await getIp().catch(() => '');
}

function setMetaResumed(status: UnwrapRef<typeof resumeStatus>) {
  form.meta.resumed = status == 'success';
}

function shouldShowVariationAlert() {
  if (!applicableVariations.value.length) return false;
  switch (currentStepType.value) {
    case 'visa_select': return true;
    case 'details': return true;
    case 'review': return true;
    default: return false;
  }
}
</script>